import { partition, orderBy, toString } from 'lodash';
import { Customizations, FuneralPlan, FuneralType, Pricing } from 'types';
import { ServiceItem } from 'types/api';
import { api, getAxiosData, customize } from 'utils';

const SERVICE_ROUTE = 'servicesv2';

interface DefaultServicesResponse {
  basic: ServiceItem[];
  optional: ServiceItem[];
}

const split = (services: ServiceItem[]) => {
  const orderedServices = orderBy(services, ['isFavorite', 'identifier'], ['desc', 'asc']);
  return partition<ServiceItem>(orderedServices, ({ optional }) => !optional);
};

const parse = (selectedServices: ServiceItem[], allServices: ServiceItem[]) => {
  const [selectedBasicServices, selectedOptionalServices] = split(selectedServices);
  const [allBasicServices, allOptionalServices] = split(allServices);

  return {
    all: {
      basic: allBasicServices,
      optional: allOptionalServices,
    },
    selected: {
      basicServices: selectedBasicServices,
      optionalServices: selectedOptionalServices,
    },
  };
};

const fetchDefault = (
  pricebookId: string,
  plan: FuneralPlan,
  type: FuneralType,
  isCompetitivePricing: boolean = false,
) => {
  const pricing: Pricing = isCompetitivePricing ? 'competitive' : 'normal';

  return api
    .get<DefaultServicesResponse>(`/${SERVICE_ROUTE}/default`, {
      params: { plan, pricebookId, pricing, type },
    })
    .then(({ data: { basic, optional } }) => ({ basic, optional }));
};

const parseServicesAlongStore = (
  selectedServices: ServiceItem[],
  defaultServices: DefaultServicesResponse,
  customizations?: Customizations,
) =>
  parse(
    selectedServices,
    customize([...defaultServices.basic, ...defaultServices.optional], customizations?.services),
  );

interface interfaceFetchSelected {
  pricebookId: string;
  plan: FuneralPlan;
  type: FuneralType;
  SKUs: string[];
  customizations?: Customizations;
  isCompetitivePricing: boolean;
}
const fetchSelected = ({
  pricebookId,
  plan,
  type,
  SKUs,
  customizations,
  isCompetitivePricing,
}: interfaceFetchSelected) =>
  Promise.all([
    SKUs.length > 0
      ? api
          .get<ServiceItem[]>(`/${SERVICE_ROUTE}`, {
            params: { SKUs: toString(SKUs), pricebookId },
          })
          .then(getAxiosData)
      : [],
    fetchDefault(pricebookId, plan, type, isCompetitivePricing),
  ]).then(([selectedServices, defaultServices]) =>
    parse(
      selectedServices,
      customize([...defaultServices.basic, ...defaultServices.optional], customizations?.services),
    ),
  );

export default {
  fetchDefault,
  fetchSelected,
  parse,
  parseServicesAlongStore,
  split,
};
